export const SOUND = {
  PREF_KEY: 'mantraz_auth_sound',
  MASTER_PEAK: 1.1,
  DRONE_PEAK: 0.22,
  DRONE_PEAK_AT: 1.15,
  DRONE_END: 3.02,
  NOTE_PEAK: 0.5,
  NOTE_DECAY: 1.8,
  NOTE_ATTACK: 0.09,
  CLOSE_AT: 2.4,
  CLOSE_PEAK: 0.55,
  CLOSE_DECAY: 1,
  CLOSE_ATTACK: 0.18,
  AUDIO_TAIL: 3.7,
  FILTER_FROM: 520,
  FILTER_TO: 1800,
  FILTER_Q: 0.7,
  RELEASE: 0.12,
};

// detiknya dipatok ke jenjang gelombang logo ke-1, ke-3 dan ke-6
const WAVE_NOTES = [
  { at: 0.12, freq: 220 },
  { at: 0.42, freq: 329.63 },
  { at: 0.72, freq: 440 },
];

const CLOSING_NOTES = [
  { freq: 110, gain: 1 },
  { freq: 164.81, gain: 0.7 },
];

const DRONE_VOICES = [
  { freq: 55, type: 'sine', detune: -5 },
  { freq: 82.41, type: 'triangle', detune: 6 },
];

// rencananya data biasa, jadi rangkaiannya bisa diperiksa tanpa AudioContext
export function buildSoundPlan() {
  const notes = WAVE_NOTES.map((note) => ({
    role: 'wave',
    at: note.at,
    freq: note.freq,
    peak: SOUND.NOTE_PEAK,
    decay: SOUND.NOTE_DECAY,
    attack: SOUND.NOTE_ATTACK,
    type: 'triangle',
  })).concat(CLOSING_NOTES.map((note) => ({
    role: 'closing',
    at: SOUND.CLOSE_AT,
    freq: note.freq,
    peak: SOUND.CLOSE_PEAK * note.gain,
    decay: SOUND.CLOSE_DECAY,
    attack: SOUND.CLOSE_ATTACK,
    type: 'sine',
  })));

  return {
    drone: {
      voices: DRONE_VOICES.map((voice) => ({ ...voice })),
      peak: SOUND.DRONE_PEAK,
      peakAt: SOUND.DRONE_PEAK_AT,
      end: SOUND.DRONE_END,
    },
    notes,
    filter: {
      type: 'lowpass',
      q: SOUND.FILTER_Q,
      from: SOUND.FILTER_FROM,
      to: SOUND.FILTER_TO,
      sweepEnd: SOUND.CLOSE_AT,
    },
  };
}

export function readSoundPref() {
  try {
    return window.localStorage.getItem(SOUND.PREF_KEY) === 'on';
  } catch (error) {
    return false;
  }
}

export function writeSoundPref(enabled) {
  try {
    window.localStorage.setItem(SOUND.PREF_KEY, enabled ? 'on' : 'off');
  } catch (error) { /* mode privat */ }
}

function safe(run) {
  try { run(); } catch (error) { /* node audio udah lepas */ }
}

export function createIntroSound() {
  let ctx = null;
  let chain = null;
  let sources = [];
  let releaseTimer = 0;

  function ensureContext() {
    if (ctx) return ctx;
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    try { ctx = new Ctor(); } catch (error) { ctx = null; }
    return ctx;
  }

  function release() {
    clearTimeout(releaseTimer);
    releaseTimer = 0;
    sources.forEach((source) => {
      safe(() => source.osc.stop());
      safe(() => source.osc.disconnect());
      safe(() => source.gain.disconnect());
    });
    sources = [];
    if (!chain) return;
    safe(() => chain.filter.disconnect());
    safe(() => chain.master.disconnect());
    safe(() => chain.limiter.disconnect());
    chain = null;
  }

  function scheduleNote(t0, note) {
    const when = t0 + note.at;
    // nada yang detiknya udah lewat dilewati, bukan diputar telat
    if (when < ctx.currentTime - 0.05) return;
    const at = Math.max(when, ctx.currentTime);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(note.peak, at + note.attack);
    gain.gain.exponentialRampToValueAtTime(0.0009, at + note.decay);
    gain.gain.linearRampToValueAtTime(0, at + note.decay + 0.08);
    gain.connect(chain.filter);

    const osc = ctx.createOscillator();
    osc.type = note.type;
    osc.frequency.setValueAtTime(note.freq, at);
    osc.connect(gain);
    osc.start(at);
    osc.stop(at + note.decay + 0.12);
    sources.push({ osc, gain });
  }

  function scheduleDrone(t0, drone) {
    const start = Math.max(ctx.currentTime, t0);
    const end = t0 + drone.end;
    if (end <= start + 0.25) return;
    const peakAt = Math.min(Math.max(t0 + drone.peakAt, start + 0.25), end - 0.5);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(drone.peak, peakAt);
    gain.gain.setValueAtTime(drone.peak, Math.max(peakAt, end - 0.85));
    gain.gain.linearRampToValueAtTime(0, end);
    gain.connect(chain.filter);

    drone.voices.forEach((voice) => {
      const osc = ctx.createOscillator();
      osc.type = voice.type;
      osc.frequency.setValueAtTime(voice.freq, start);
      osc.detune.setValueAtTime(voice.detune, start);
      osc.connect(gain);
      osc.start(start);
      osc.stop(end + 0.08);
      sources.push({ osc, gain });
    });
  }

  function build(offset) {
    if (!ctx || ctx.state !== 'running') return;
    release();

    const plan = buildSoundPlan();
    const t0 = ctx.currentTime - offset;
    const filter = ctx.createBiquadFilter();
    const master = ctx.createGain();
    const limiter = ctx.createDynamicsCompressor();

    filter.type = plan.filter.type;
    filter.Q.setValueAtTime(plan.filter.q, ctx.currentTime);
    master.gain.setValueAtTime(SOUND.MASTER_PEAK, ctx.currentTime);
    // limiter di ujung rantai, nahan puncak biar gak clip pas semua sumber numpuk
    limiter.threshold.setValueAtTime(-2, ctx.currentTime);
    limiter.ratio.setValueAtTime(20, ctx.currentTime);
    limiter.knee.setValueAtTime(0, ctx.currentTime);
    limiter.attack.setValueAtTime(0.001, ctx.currentTime);
    limiter.release.setValueAtTime(0.05, ctx.currentTime);
    filter.connect(master);
    master.connect(limiter);
    limiter.connect(ctx.destination);
    chain = { filter, master, limiter };

    const sweepFrom = Math.max(ctx.currentTime, t0);
    filter.frequency.setValueAtTime(plan.filter.from, sweepFrom);
    filter.frequency.linearRampToValueAtTime(
      plan.filter.to,
      Math.max(sweepFrom + 0.2, t0 + plan.filter.sweepEnd),
    );

    scheduleDrone(t0, plan.drone);
    plan.notes.forEach((note) => scheduleNote(t0, note));

    clearTimeout(releaseTimer);
    releaseTimer = setTimeout(release, Math.max(250, (SOUND.AUDIO_TAIL - offset) * 1000));
  }

  function start(offsetSeconds) {
    const offset = offsetSeconds > 0 ? offsetSeconds : 0;
    if (offset >= SOUND.AUDIO_TAIL) return;
    const audio = ensureContext();
    if (!audio) return;

    let resumed = null;
    try { resumed = audio.resume(); } catch (error) { resumed = null; }
    if (!resumed || typeof resumed.then !== 'function') {
      build(offset);
      return;
    }
    // autoplay bisa ditolak diam-diam, jadi state-nya dicek lagi sesudah resume
    resumed.then(() => build(offset)).catch(() => { /* tanpa gestur, intro jalan bisu */ });
  }

  function stop() {
    if (!ctx || !chain) { release(); return; }
    const now = ctx.currentTime;
    const { gain } = chain.master;
    // diturunin dulu biar gak ada klik
    safe(() => {
      gain.cancelScheduledValues(now);
      gain.setValueAtTime(gain.value, now);
      gain.linearRampToValueAtTime(0, now + SOUND.RELEASE);
    });
    clearTimeout(releaseTimer);
    releaseTimer = setTimeout(release, SOUND.RELEASE * 1000 + 60);
  }

  function destroy() {
    stop();
    release();
    if (!ctx) return;
    safe(() => ctx.close());
    ctx = null;
  }

  return { start, stop, destroy };
}
